// ═══════════════════════════════════════════════════════════════════════════
// analytics.js — single tiny event tracker for retention metrics.
//
// Mini-spec #5 introduced a list of events to surface ("daily_refill_consumed",
// "streak_broken", "episode_card_shared", etc.). Until a real analytics
// pipeline (PostHog / Amplitude / Mixpanel) is wired, every track() call
// writes a row to public.analytics_events in Supabase. That gives us
// the data trail without a third-party dependency. Pulling it into a
// proper dashboard later is a one-line swap of the writer below.
//
// Calls are fire-and-forget. We never await track() from a render path.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

// Canonical event names — anything outside this list logs a console
// warning in dev so we don't sprawl. Add to the set when you add a
// new tracked moment in product code.
export const EVENTS = {
  DAILY_REFILL_CONSUMED:           'daily_refill_consumed',
  STREAK_BROKEN:                   'streak_broken',
  STREAK_MILESTONE:                'streak_milestone',
  EPISODE_CARD_SHARED:             'episode_card_shared',
  MORNING_BRIEFING_OPENED:         'morning_briefing_opened',
  MORNING_BRIEFING_CLICKED_GENERATE: 'morning_briefing_clicked_generate',
  LEADERBOARD_VIEWED:              'leaderboard_viewed',
  TIME_SAVED_MILESTONE:            'time_saved_milestone',
  UPGRADE_MODAL_SHOWN:             'upgrade_modal_shown',
  UPGRADE_MODAL_CLICKED_PRIMARY:   'upgrade_modal_clicked_primary',
  UPGRADE_MODAL_DISMISSED:         'upgrade_modal_dismissed',
  AI_COVER_ART_GENERATED:          'ai_cover_art_generated',
  CREDIT_TOPUP_INITIATED:          'credit_topup_initiated',
  // Mini-spec #6: hook scoring
  HOOK_SCORED:                     'hook_scored',
  HOOK_SCORE_MILESTONE:            'hook_score_milestone',
  // Mini-spec #7: daily morning briefing
  MORNING_BRIEFING_UNSUBSCRIBED:   'morning_briefing_unsubscribed',
  // Mini-spec #8: episode cards + Drew's Pick
  DREWS_PICK_SET:                  'drews_pick_set',
  DREWS_PICK_VIEWED:               'drews_pick_viewed',
  // Mini-spec #9: public hook leaderboard
  LEADERBOARD_FILTER_APPLIED:      'leaderboard_filter_applied',
  LEADERBOARD_CREATOR_CLICKED:     'leaderboard_creator_clicked',
};

const KNOWN = new Set(Object.values(EVENTS));

/**
 * Fire-and-forget. Logs the event with optional properties.
 * Never blocks the caller. Errors are swallowed silently in prod.
 */
export function track(eventName, properties = {}, userId = null) {
  if (!eventName) return;
  if (!KNOWN.has(eventName) && process.env.NODE_ENV !== 'production') {
    console.warn(`track: unregistered event "${eventName}" — add it to EVENTS in analytics.js`);
  }
  // Fire-and-forget — explicitly don't await.
  (async () => {
    try {
      if (!supabase) return;
      const { data: { user } } = await supabase.auth.getUser();
      const uid = userId || user?.id || null;
      await supabase.from('analytics_events').insert({
        user_id:    uid,
        event_name: eventName,
        properties: properties || {},
      });
    } catch {
      // Swallow — tracking must never break a user flow.
    }
  })();
}
